import {
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { Button, EmbeddedPagination, toast } from '@scholastic/mockingjay';
import SortSolid from '@data/assets/sort-solid.svg';
import SortSolidUp from '@data/assets/sort-solid-up.svg';
import SortSolidDown from '@data/assets/sort-solid-down.svg';
import CaretDownIcon from '@data/assets/caret-down.svg';
import DocumentCsvIcon from '@data/assets/document-csv-solid.svg';
import MagnifyingGlassIcon from '@data/assets/magnifying-glass-solid.svg';
import { formatTableData } from '@data/utils/format-table-data';
import {
  DATA_ERROR_MESSAGE,
  DATA_UNAVAILABLE_MESSAGE,
  EXPORT_ERROR_MESSAGE,
  type ColumnGroup,
  type TableColumn,
  type TableProps,
  type TableRow,
  type TableToolbarProps,
} from '@data/components/basic/Table/types';
import { LoadingBar } from '@data/components/basic/LoadingBar/LoadingBar';

function TableToolbar({
  loading,
  isSearchEmpty,
  searchValue,
  searchPlaceholder,
  onSearchChange,
  onSearchKeyDown,
  onSearchSubmit,
  onExportClick,
  actions,
}: TableToolbarProps) {
  if (loading) {
    return (
      <div className="flex flex-row items-center border-b border-neutral-400 p-3">
        <LoadingBar className="mx-0 h-12 w-64" />
      </div>
    );
  }
  return (
    <div className="flex min-w-0 flex-wrap items-center gap-3 p-3 @[500px]/toolbar:flex-nowrap @[500px]/toolbar:justify-between">
      <div className="flex min-w-[120px] flex-1 flex-row gap-3">
        <div className="relative min-w-0 flex-1 @[500px]/toolbar:max-w-[320px]">
          <MagnifyingGlassIcon
            aria-hidden="true"
            className={`pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 ${
              isSearchEmpty ? 'text-neutral-400' : 'text-neutral-1100'
            }`}
          />
          <input
            type="text"
            placeholder={searchPlaceholder}
            value={searchValue ?? ''}
            onChange={(e) => onSearchChange?.(e.target.value)}
            onKeyDown={onSearchKeyDown}
            className="
              h-12 w-full min-w-0 text-ellipsis rounded-sm border border-neutral-400 bg-white pl-10 pr-4 text-base
              font-normal text-neutral-1100 outline-none focus:border-neutral-1100"
          />
        </div>
        <Button
          variant="primary"
          onClick={onSearchSubmit}
          disabled={isSearchEmpty}
          aria-label="Search"
          className="!h-12 !w-12 !min-w-12 !space-x-0 !rounded-full @[500px]/toolbar:!w-28 @[500px]/toolbar:!min-w-[120px] @[500px]/toolbar:!rounded-full @[500px]/toolbar:!px-4"
        >
          <MagnifyingGlassIcon
            aria-hidden="true"
            className="h-6 w-6 @[500px]/toolbar:hidden"
          />
          <span className="hidden @[500px]/toolbar:inline">Search</span>
        </Button>
      </div>
      {/* Narrow, actions should add `order-last w-full` to sit under search and export. */}
      {actions}
      <Button
        variant="tertiary"
        aria-label="Export table"
        className="!size-11 !min-w-0 shrink-0 !space-x-0 !p-0 hover:!bg-white @[500px]/toolbar:!w-auto @[500px]/toolbar:!px-4 [&>span]:inline-flex [&>span]:items-center [&>span]:gap-1 [@media(hover:hover)]:hover:!bg-neutral-100"
        onClick={onExportClick}
      >
        <DocumentCsvIcon aria-hidden="true" className="h-4 w-4" />
        <span className="hidden @[500px]/toolbar:inline">Export table</span>
      </Button>
    </div>
  );
}

/** A page's own drawing of the cell, else its formatted value. */
function cellContent(
  renderCell: TableProps['renderCell'],
  value: TableRow[number],
  column: TableColumn,
  row: TableRow,
  depth: number
): ReactNode {
  return (
    renderCell?.(value, column, row, depth) ??
    formatTableData(value, column.type)
  );
}

type RowId = string | number;

/**
 * The rows as drawn: each row followed, when expanded, by its children, so a
 * nested table renders in one flat pass.
 */
function useDisplayRows(
  rows: TableRow[],
  childRows: Map<RowId, TableRow[]> | undefined,
  idIndex: number
) {
  const [expandedIds, setExpandedIds] = useState<Set<RowId>>(() => new Set());

  const toggleExpanded = (rowId: RowId) =>
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });

  const displayRows: { row: TableRow; depth: number; rowId: RowId }[] = [];
  const addRows = (list: TableRow[], depth: number) => {
    list.forEach((row, index) => {
      const rowId = idIndex >= 0 ? (row[idIndex] as RowId) : index;
      displayRows.push({ row, depth, rowId });
      if (expandedIds.has(rowId)) {
        addRows(childRows?.get(rowId) ?? [], depth + 1);
      }
    });
  };
  addRows(rows, 0);

  return {
    displayRows,
    expandedIds,
    toggleExpanded,
    hasNestedRows: !!childRows?.size,
  };
}

/**
 * The first cell of a row: a drill-down link, or — in a nested table — the
 * row's expand toggle, indented to its depth.
 */
function FirstCell({
  content,
  label,
  depth,
  nested,
  hasChildren,
  isExpanded,
  onToggle,
  rowId,
  onDrilldown,
}: {
  content: ReactNode;
  label: string;
  depth: number;
  nested: boolean;
  hasChildren: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  rowId: RowId;
  onDrilldown: TableProps['onDrilldown'];
}) {
  if (nested) {
    return (
      <div
        className="flex items-center gap-2"
        style={{ paddingLeft: `${depth * 24}px` }}
      >
        {hasChildren ? (
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={isExpanded}
            aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${label}`}
            className="flex size-4 shrink-0 items-center justify-center"
          >
            <CaretDownIcon
              aria-hidden="true"
              className={`size-3 transition-transform ${
                isExpanded ? '' : '-rotate-90'
              }`}
            />
          </button>
        ) : (
          <span className="size-4 shrink-0" />
        )}
        {/* An open group shows its whole name: on touch that is the only way to reach it. */}
        <span
          className={`${depth === 0 ? 'font-semibold' : ''} ${
            depth === 0 && isExpanded ? '' : 'line-clamp-2'
          }`}
        >
          {content}
        </span>
      </div>
    );
  }

  if (onDrilldown) {
    return (
      <button
        type="button"
        onClick={() => onDrilldown(rowId)}
        className="text-left text-[#0E67E3]"
      >
        {content}
      </button>
    );
  }

  return content;
}

/**
 * What an empty table says. Naming the submitted search rather than the input
 * keeps the message describing the rows that are actually missing.
 */
function noRowsMessage(searchTerms: string | undefined) {
  const submitted = searchTerms?.trim();
  if (!submitted) return DATA_UNAVAILABLE_MESSAGE;
  return `No entries found matching "${submitted}"`;
}

export function Table({
  columns,
  rows,
  totalCount,
  sortColumn,
  sortDirection,
  onSortChange,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  searchValue,
  searchTerms,
  onSearchChange,
  onSearchSubmit,
  onDrilldown,
  onExportToCSV,
  columnGroups,
  borderedCells,
  leftAlignedColumns,
  childRows,
  renderCell,
  toolbarActions,
  toolbarCaption,
  keepToolbarWhileLoading,
  scrollRegionLabel,
  loading,
  error,
}: TableProps) {
  const idIndex = columns.findIndex((c) => c.type === 'id');
  const { displayRows, expandedIds, toggleExpanded, hasNestedRows } =
    useDisplayRows(rows, childRows, idIndex);
  const visibleColumns = columns
    .map((col, index) => ({ col, index }))
    .filter(({ col }) => col.type !== 'id' && col.type !== 'group');

  const hasColumnGroups = columnGroups && columnGroups.length > 0;
  const cellBorderClass = borderedCells
    ? 'border-b border-r border-neutral-200 last:border-r-0'
    : '';

  // Named for the first column shown, which is the one a reader searches by.
  const searchPlaceholder = `Search ${
    visibleColumns[0]?.col.label.toLowerCase() ?? ''
  }`;
  const isSortable = (col: TableColumn) => col.sortable !== false;
  const isLeftAligned = (col: TableColumn) =>
    leftAlignedColumns?.includes(col.key) ?? false;
  const headerAlignClass = (visibleIndex: number, col: TableColumn) => {
    if (visibleIndex === 0) return 'justify-between';
    return isLeftAligned(col)
      ? 'justify-start text-left'
      : 'justify-end text-right';
  };

  const handleSortClick = (col: TableColumn) => {
    if (!isSortable(col) || !onSortChange) {
      return;
    }
    // 3-state cycle: not sorted → asc → desc
    if (sortColumn !== col.key) {
      onSortChange(col.key, 'asc');
    } else if (sortDirection === 'asc') {
      onSortChange(col.key, 'desc');
    } else {
      onSortChange(undefined, undefined);
    }
  };

  const renderSortIcon = (col: TableColumn) => {
    if (!isSortable(col)) {
      return null;
    }
    if (sortColumn === col.key) {
      if (sortDirection === 'asc') {
        return <SortSolidUp />;
      }
      if (sortDirection === 'desc') {
        return <SortSolidDown />;
      }
    }
    return <SortSolid />;
  };

  const isSearchEmpty = !searchValue || searchValue.trim() === '';
  // The rows answer the submitted search, so the empty state names that one.
  const emptyMessage = noRowsMessage(searchTerms);

  const handleSearchSubmit = () => {
    if (isSearchEmpty) {
      return;
    }
    onSearchSubmit?.(searchValue ?? '');
  };

  const handleSearchKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearchSubmit();
    }
  };

  const handleExportClick = () => {
    onExportToCSV().catch(() => {
      toast.error(EXPORT_ERROR_MESSAGE, {
        cancel: { label: 'Close' },
        duration: 5000,
      });
    });
  };

  // Cache the last known good totalCount so EmbeddedPagination doesn't briefly
  // see totalCount = 0 during a refetch (which would make its internal
  // `paginationRange.length < 2` early-return unmount the <Select> children
  // and reset the rows-per-page dropdown back to its default).
  const lastTotalCountRef = useRef(totalCount);
  useEffect(() => {
    if (!loading && totalCount > 0) {
      lastTotalCountRef.current = totalCount;
    }
  }, [loading, totalCount]);
  const effectiveTotalCount = loading ? lastTotalCountRef.current : totalCount;

  // Mirrors EmbeddedPagination's own early-return so we don't render an empty
  // wrapper div when EmbeddedPagination would render null.
  const showPagination = !error && page !== 0 && effectiveTotalCount > 10;

  // Skeleton table for the loading state: 2 columns on mobile, 5 on tablet+
  const renderSkeletonTable = (colCount: number) => (
    <table className="w-full min-w-[826px]">
      <thead>
        <tr className="bg-white">
          {Array.from({ length: colCount }).map((_, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <th key={i} className="p-4">
              <LoadingBar className="mx-0 h-5 w-full" />
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {Array.from({ length: pageSize }).map((_, rowIdx) => (
          // eslint-disable-next-line react/no-array-index-key
          <tr key={rowIdx} className="border-t border-neutral-100">
            {Array.from({ length: colCount }).map((__, colIdx) => (
              // eslint-disable-next-line react/no-array-index-key
              <td key={colIdx} className="p-3">
                <LoadingBar className="mx-0 h-5 w-full" />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );

  // Renders a group header cell. Empty-label cells use <td> (not <th>) so
  // screen readers don't announce an empty column header (WCAG 1.3.1).
  const renderGroupHeaderCell = (group: ColumnGroup, idx: number) => {
    if (group.label) {
      return (
        <th
          key={`${group.label}-${idx}`}
          colSpan={group.colSpan}
          className={`p-4 text-center text-sm font-semibold ${cellBorderClass}`}
        >
          {group.label}
        </th>
      );
    }
    return (
      <td
        key={`spacer-${idx}`}
        colSpan={group.colSpan}
        aria-hidden="true"
        className={`p-4 ${cellBorderClass}`}
      />
    );
  };

  if (error) {
    return (
      <div className="w-full min-w-0 overflow-hidden rounded-lg border border-neutral-400 antialiased @container/toolbar">
        <div className="border-1 mx-4 my-20 rounded-lg border border-dashed border-neutral-400 p-8 text-center text-neutral-700 @[350px]/toolbar:mx-16">
          <span>{DATA_ERROR_MESSAGE}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-w-0 overflow-hidden rounded-lg border border-neutral-400 antialiased @container/toolbar">
      {/* Search and export container */}
      <TableToolbar
        loading={loading && !keepToolbarWhileLoading}
        isSearchEmpty={isSearchEmpty}
        searchValue={searchValue}
        searchPlaceholder={searchPlaceholder}
        onSearchChange={onSearchChange}
        onSearchKeyDown={handleSearchKeyDown}
        onSearchSubmit={handleSearchSubmit}
        onExportClick={handleExportClick}
        actions={toolbarActions}
      />
      {toolbarCaption && <div className="px-3 pb-3">{toolbarCaption}</div>}
      {/* Column headers and data */}
      <div
        // Relative so cells' sr-only text is clipped here, not widening the page.
        className="relative overflow-x-auto border-t border-neutral-400 focus-ring"
        {...(scrollRegionLabel && {
          role: 'region',
          'aria-label': scrollRegionLabel,
          tabIndex: 0,
        })}
      >
        {loading && (
          <>
            <div className="tablet:hidden">{renderSkeletonTable(2)}</div>
            <div className="hidden tablet:block">{renderSkeletonTable(5)}</div>
          </>
        )}
        {!loading && (
          <table className="w-full min-w-[826px]">
            <thead>
              {hasColumnGroups && (
                <tr className="bg-purple-50">
                  {columnGroups.map((group: ColumnGroup, idx: number) =>
                    renderGroupHeaderCell(group, idx)
                  )}
                </tr>
              )}
              <tr className="bg-purple-50">
                {visibleColumns.map(({ col }, visibleIndex) => (
                  <th
                    key={col.key}
                    className={`${
                      visibleIndex === 0 ? 'min-w-[200px]' : 'max-w-[180px]'
                    } p-4 text-sm font-semibold ${cellBorderClass}`}
                  >
                    <div
                      className={`flex items-center ${headerAlignClass(
                        visibleIndex,
                        col
                      )} gap-2`}
                    >
                      {col.label}
                      {isSortable(col) && (
                        <button
                          className="flex flex-row"
                          type="button"
                          onClick={() => handleSortClick(col)}
                          aria-label={`Sort by ${col.label}`}
                        >
                          <div className="mx-1 w-[15px]">
                            {renderSortIcon(col)}
                          </div>
                        </button>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            {!rows.length ? (
              <tbody>
                <tr>
                  <td colSpan={visibleColumns.length}>
                    <div className="border-1 mx-16 my-20 rounded-lg border border-dashed border-neutral-400 p-8 text-center text-neutral-700">
                      <span>{emptyMessage}</span>
                    </div>
                  </td>
                </tr>
              </tbody>
            ) : (
              <tbody>
                {displayRows.map(({ row, depth, rowId }, rowIndex) => {
                  const isLastRow =
                    borderedCells && rowIndex === displayRows.length - 1;
                  return (
                    <tr
                      key={String(rowId)}
                      // Nested rows share the divider's grey, so theirs is a shade darker.
                      className={`border-t hover:bg-neutral-100 active:bg-neutral-100 ${
                        depth > 0
                          ? 'border-neutral-200 bg-neutral-100'
                          : 'border-neutral-100'
                      }`}
                    >
                      {visibleColumns.map(({ col, index }, visibleIndex) => {
                        const value = row[index];
                        const content = cellContent(
                          renderCell,
                          value,
                          col,
                          row,
                          depth
                        );
                        const isFirst = visibleIndex === 0;
                        return (
                          <td
                            key={col.key}
                            className={`p-3 text-neutral-900 ${
                              isFirst || isLeftAligned(col)
                                ? 'text-left'
                                : 'text-right'
                            } ${cellBorderClass}${
                              isLastRow ? ' border-b-0' : ''
                            }`}
                          >
                            {isFirst ? (
                              <FirstCell
                                content={content}
                                label={formatTableData(value, col.type)}
                                depth={depth}
                                nested={hasNestedRows}
                                hasChildren={!!childRows?.get(rowId)?.length}
                                isExpanded={expandedIds.has(rowId)}
                                onToggle={() => toggleExpanded(rowId)}
                                rowId={rowId}
                                onDrilldown={onDrilldown}
                              />
                            ) : (
                              content
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            )}
          </table>
        )}
      </div>
      {showPagination || loading ? (
        <div className="px-4 py-5 [&_.inline-grid]:grid-rows-1 [&_.inline-grid]:items-center [&_.inline-grid_.row-start-2]:row-start-1">
          <div className={loading || !showPagination ? 'hidden' : ''}>
            <EmbeddedPagination
              totalCount={effectiveTotalCount}
              page={page}
              onPageChange={onPageChange}
              onRowsChange={(newRows) => {
                onPageSizeChange(newRows);
                onPageChange(1);
              }}
            />
          </div>
          {loading && (
            <div className="flex flex-row items-center justify-between gap-3">
              <LoadingBar className="mx-0 h-12 w-full self-center tablet:w-[256px]" />
              <LoadingBar className="mx-0 h-8 w-full self-center tablet:w-[368px]" />
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}
